/**
 * `defineBlock` / `defineMark` and the content-expression engine of the block registry (plan §8.6).
 *
 * A block is defined once: Zod attributes with defaults, a ProseMirror content expression, groups,
 * publication policy, expression sites, plain-text, Markdown and HTML codecs and an AI description.
 * Document validation, tiptap attributes (`tiptapAttrsFromSchema`), the insert palette, Markdown
 * export and HTML import are all generated from these definitions, so they cannot drift.
 *
 * Owner: doc-engine (wave 1), page-editor (wave 2).
 */
import { type z } from 'zod';
import type { LocalizedText } from '../localized-text';
import type { ExprSite } from './expr-site';
import type { DocumentNode } from './node';

export type BlockCategory = 'writing' | 'layout' | 'media' | 'visuals' | 'navigation' | 'data' | 'interface';

/**
 * What happens to a block on a public page: `strip` removes it, `snapshot` freezes its data at
 * publication time, `live` renders it as is (the content is already static or publicly safe).
 */
export type PublishPolicy = 'strip' | 'snapshot' | 'live';

/** A parsed HTML element as seen by import rules (DOM independent, so it runs on the server too). */
export type HtmlElementView = {
  tag: string;
  attrs: Readonly<Record<string, string>>;
  style: Readonly<Record<string, string>>;
  classes: readonly string[];
  /** Text content of the element (whitespace kept). */
  text: string;
};

export type HtmlRule = {
  /** Lower-case tag name. */
  tag: string;
  /** Extra condition, e.g. a data attribute. */
  match?: (el: HtmlElementView) => boolean;
  /** Attributes of the created node; the result is parsed by the block's schema. */
  attrs?: (el: HtmlElementView) => Record<string, unknown>;
  /** Higher wins when several rules match one element (default 50). */
  priority?: number;
};

export type MarkdownContext = {
  /** Inline content of a node (text with marks, hard breaks, inline nodes). */
  inline(node: DocumentNode): string;
  /** Block children, separated by blank lines. */
  blocks(nodes: readonly DocumentNode[] | undefined): string;
  /** Plain text of a node (registry `toText`). */
  text(node: DocumentNode): string;
};

export type TextContext = {
  /** Plain text of the node's children (blocks separated by new lines). */
  children(node: DocumentNode): string;
};

export type ValidateContext = {
  parent: DocumentNode | null;
  path: (string | number)[];
  fail(message: string, code?: string): void;
};

/** Attributes schema of a block: a strict Zod object. */
export type AttrsSchema = z.ZodObject<z.ZodRawShape>;

export type BlockDefinition<S extends AttrsSchema = AttrsSchema> = {
  name: string;
  /** Strict Zod object for the node's `attrs` (an absent `attrs` parses as `{}`). */
  attrs: S;
  /** Values for a newly inserted block (must parse with `attrs` unless `requiresSetup`). */
  defaults: z.input<S>;
  /**
   * Inserting the block opens its inspector first (a data source, a field, a file, a link):
   * `defaults` is an incomplete draft and is not saved until it validates.
   */
  requiresSetup?: boolean;
  /** ProseMirror content expression (`'inline*'`, `'block+'`, `'tableRow+'`); absent = leaf. */
  content?: string;
  /** Space separated ProseMirror groups (`'block'`, `'inline'`). */
  group?: string;
  inline?: boolean;
  /** Leaf rendered as one unit (data blocks, images, mentions). */
  atom?: boolean;
  /** Allowed marks: absent = all, `''` = none. */
  marks?: string;
  publish: PublishPolicy;
  /** Expression sites inside `attrs` (paths relative to attrs). */
  expressions?: (attrs: z.output<S>) => ExprSite[];
  toText?: (node: DocumentNode, ctx: TextContext) => string;
  toMarkdown?: (node: DocumentNode, ctx: MarkdownContext) => string;
  fromHTML?: HtmlRule[];
  /** One or two sentences that tell an AI model when and how to use the block. */
  aiDescription: string;
  label: LocalizedText;
  description?: LocalizedText;
  category?: BlockCategory;
  /**
   * Present when the shipped insert menu offers the block (order = position; `compact` = offered
   * on compact surfaces; `recordInterface` = only in record interfaces). New model-only blocks
   * leave it out until their editor support lands.
   */
  palette?: { order: number; compact?: boolean; recordInterface?: boolean };
  /**
   * The shipped editors open and save this node. Model-only nodes (new blocks whose editor support
   * lands later) leave it out: the model validates them, but `BlockDocument` (what the API
   * stores) refuses them, so no editor can open a document it would silently empty.
   */
  supported?: boolean;
  /** Kept readable for stored content; new content uses `replacedBy`. */
  deprecated?: { replacedBy: string };
  /** Message when the children do not match `content`. */
  contentMessage?: string;
  /** Structural checks beyond attributes and content. */
  validate?: (node: DocumentNode, attrs: z.output<S>, ctx: ValidateContext) => void;
  /**
   * Fit already repaired children to `content` (`repairDocument`): return the fixed node, or
   * `null` to unwrap the node into its children. Generic fixes (inline runs wrapped in
   * paragraphs, empty containers filled) run first.
   */
  repair?: (node: DocumentNode) => DocumentNode | null;
  /**
   * How the block degrades on a surface that does not support it (pasting into an older editor,
   * AI output for a narrower surface). Default: containers unwrap into their children, inline
   * atoms become their text, block atoms are dropped.
   */
  degrade?: (node: DocumentNode) => DocumentNode[];
};

export function defineBlock<S extends AttrsSchema>(definition: BlockDefinition<S>): BlockDefinition<S> {
  const shape = definition.attrs.shape as Record<string, unknown>;
  const unknownKeys = Object.keys(definition.defaults as object).filter((k) => !(k in shape));
  if (unknownKeys.length)
    throw new Error(`Block "${definition.name}": unknown default attributes ${unknownKeys.join(', ')}`);
  if (!definition.requiresSetup && !definition.attrs.safeParse(definition.defaults).success)
    throw new Error(`Block "${definition.name}": defaults do not match the attributes schema`);
  return definition;
}

export type MarkDefinition<S extends AttrsSchema = AttrsSchema> = {
  name: string;
  attrs: S;
  defaults: z.input<S>;
  fromHTML?: HtmlRule[];
  /** Markdown delimiters; marks without them are dropped in Markdown. */
  markdown?: {
    open: string | ((attrs: z.output<S>) => string);
    close: string | ((attrs: z.output<S>) => string);
  };
  label: LocalizedText;
  /** The shipped editors apply and keep this mark (see `BlockDefinition.supported`). */
  supported?: boolean;
};

export function defineMark<S extends AttrsSchema>(definition: MarkDefinition<S>): MarkDefinition<S> {
  if (!definition.attrs.safeParse(definition.defaults).success)
    throw new Error(`Mark "${definition.name}": defaults do not match the attributes schema`);
  return definition;
}

// -------------------------------------------------------------------------------------------------
// Content expressions: a regular language over child types, compiled to a RegExp.
//   expr   := seq ('|' seq)*
//   seq    := term+
//   term   := atom ('*' | '+' | '?' | '{n}' | '{n,}' | '{n,m}')?
//   atom   := name | '(' expr ')'
// A name is a node type or a group. Children are encoded as `type;type;…`.
// -------------------------------------------------------------------------------------------------

type Token = { kind: 'name' | 'op'; value: string };

function tokenize(expr: string): Token[] {
  const tokens: Token[] = [];
  const re = /\s*(?:([A-Za-z][A-Za-z0-9_]*)|(\{\d+(?:,\d*)?\})|([()|*+?]))/y;
  let i = 0;
  while (i < expr.length) {
    if (/\s/.test(expr[i]!)) {
      i++;
      continue;
    }
    re.lastIndex = i;
    const m = re.exec(expr);
    if (!m) throw new Error(`Invalid content expression "${expr}" at ${i}`);
    tokens.push(m[1] ? { kind: 'name', value: m[1] } : { kind: 'op', value: (m[2] ?? m[3])! });
    i = re.lastIndex;
  }
  return tokens;
}

/** Compile a content expression; `resolve` expands a name to the node types it stands for. */
export function compileContentExpression(expr: string, resolve: (name: string) => string[]): RegExp {
  const tokens = tokenize(expr);
  let pos = 0;
  const peek = () => tokens[pos];
  const parseChoice = (): string => {
    const options = [parseSeq()];
    while (peek()?.value === '|') {
      pos++;
      options.push(parseSeq());
    }
    return options.length === 1 ? options[0]! : `(?:${options.join('|')})`;
  };
  const parseSeq = (): string => {
    let out = '';
    while (pos < tokens.length && peek()!.value !== '|' && peek()!.value !== ')') out += parseTerm();
    if (!out) throw new Error(`Empty sequence in content expression "${expr}"`);
    return out;
  };
  const parseTerm = (): string => {
    const t = tokens[pos++]!;
    let atom: string;
    if (t.kind === 'name') {
      const types = resolve(t.value);
      if (!types.length) throw new Error(`Unknown node type or group "${t.value}" in "${expr}"`);
      atom = `(?:${types.map((n) => `${n};`).join('|')})`;
    } else if (t.value === '(') {
      atom = `(?:${parseChoice()})`;
      if (tokens[pos++]?.value !== ')') throw new Error(`Missing ")" in content expression "${expr}"`);
    } else throw new Error(`Unexpected "${t.value}" in content expression "${expr}"`);
    const q = peek();
    if (q && (q.value === '*' || q.value === '+' || q.value === '?' || q.value.startsWith('{'))) {
      pos++;
      atom += q.value;
    }
    return atom;
  };
  const body = parseChoice();
  if (pos !== tokens.length)
    throw new Error(`Unexpected "${tokens[pos]!.value}" in content expression "${expr}"`);
  return new RegExp(`^${body}$`);
}

/** Does a list of child types match a compiled expression? */
export function matchesContent(compiled: RegExp, childTypes: readonly string[]): boolean {
  return compiled.test(childTypes.map((t) => `${t};`).join(''));
}

// -------------------------------------------------------------------------------------------------
// tiptap attribute derivation
// -------------------------------------------------------------------------------------------------

export type TiptapAttributeSpec = { default: unknown };

/** The default of one attribute: explicit block default, else the schema default, else null. */
function attributeDefault(schema: z.ZodType, explicit: unknown, hasExplicit: boolean): unknown {
  // An attribute without a default is null when the schema allows null, else absent (undefined
  // never reaches JSON), so a node tiptap fills with defaults still validates.
  const empty = schema.safeParse(null).success ? null : undefined;
  if (hasExplicit) return explicit === undefined ? empty : explicit;
  const def = (schema as z.ZodType & { def?: { type?: string; defaultValue?: unknown } }).def;
  if (def?.type === 'default') {
    const value = def.defaultValue;
    return typeof value === 'function' ? (value as () => unknown)() : value;
  }
  return empty;
}

/**
 * tiptap `addAttributes()` config derived from a block's Zod attributes: one entry per key of the
 * schema, defaulting to the block default (or the schema default, or null). Use as
 * `addAttributes: () => tiptapAttrsFromSchema(block)`.
 */
export function tiptapAttrsFromSchema(
  block: Pick<BlockDefinition, 'attrs' | 'defaults'>,
): Record<string, TiptapAttributeSpec> {
  const shape = block.attrs.shape as Record<string, z.ZodType>;
  const defaults = (block.defaults ?? {}) as Record<string, unknown>;
  return Object.fromEntries(
    Object.entries(shape).map(([key, schema]) => [
      key,
      { default: structuredCloneSafe(attributeDefault(schema, defaults[key], key in defaults)) },
    ]),
  );
}

function structuredCloneSafe<T>(value: T): T {
  return value === undefined || value === null || typeof value !== 'object' ? value : structuredClone(value);
}
